import { useCallback, useEffect, useRef, useState } from "react";
import type { NotaFiscal } from "@/data/mock";
import { api, ApiError } from "@/lib/api";
import { toast } from "sonner";
import { useNF } from "./NFContext";

const POLL_INTERVAL_MS = 4000;
/** Status enquanto o OCR valida a NF em segundo plano. */
export const STATUS_AGUARDANDO_OCR = "A validar";

/**
 * Mantém uma tela de NFs atualizada: recarrega quando o contexto avisa
 * (`versao`) e, enquanto `aguardandoOCR` (alguma NF visível em "A validar"),
 * a cada 4s até o OCR terminar.
 */
export function useAtualizacaoNF(recarregar: () => unknown, aguardandoOCR: boolean) {
  const { versao } = useNF();
  const recarregarRef = useRef(recarregar);
  useEffect(() => {
    recarregarRef.current = recarregar;
  });

  // Só nas mudanças de versão — a carga inicial é da própria tela.
  const versaoInicial = useRef(versao);
  useEffect(() => {
    if (versao !== versaoInicial.current) void recarregarRef.current();
  }, [versao]);

  useEffect(() => {
    if (!aguardandoOCR) return;
    const t = setInterval(() => void recarregarRef.current(), POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [aguardandoOCR]);
}

/**
 * NF do detalhe, buscada em GET /nfs/:id (com histórico). `nf` é null
 * enquanto carrega e quando não existe ou não é acessível (404/403).
 * `atualizar` aplica a NF devolvida por uma ação (aprovar, recusar...).
 */
export function useNFDetalhe(id: string | undefined) {
  // Guarda o id junto: ao trocar de NF, a anterior deixa de valer já neste render.
  const [estado, setEstado] = useState<{ id: string; nf: NotaFiscal | null } | null>(null);
  const seq = useRef(0);

  const recarregar = useCallback(async () => {
    if (!id) return;
    const minha = ++seq.current;
    try {
      const nf = await api.get<NotaFiscal>(`/nfs/${id}`);
      if (minha === seq.current) setEstado({ id, nf });
    } catch (err) {
      if (minha !== seq.current) return;
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        setEstado({ id, nf: null });
      } else {
        // Falha passageira (ex.: durante o polling) mantém a NF que já estava na tela.
        setEstado((atual) => (atual?.id === id ? atual : { id, nf: null }));
        toast.error(err instanceof Error ? err.message : "Erro ao carregar a NF");
      }
    }
  }, [id]);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  const nf = estado?.id === id ? estado.nf : null;
  useAtualizacaoNF(recarregar, nf?.status === STATUS_AGUARDANDO_OCR);

  const atualizar = useCallback(
    (atualizada: NotaFiscal) => setEstado({ id: atualizada.id, nf: atualizada }),
    [],
  );

  return { nf, carregando: !!id && estado?.id !== id, atualizar, recarregar };
}
